<script>
import get from 'lodash/get'
import set from 'lodash/set'
import flatten from 'lodash/flatten'
import cloneDeep from 'lodash/cloneDeep'
import { mask } from 'vue-the-mask'
import { mapGetters, mapActions, mapState } from 'vuex'
import productMixin from '@/mixins/edit-related-products.mixin.js'
import ProductsEdit from '@/components/related-products-edit'

const phoneRE = /^04[\d]{8}$/

export default {
  name: 'UserForm',
  components: {
    ProductsEdit,
  },
  directives: {
    mask,
  },
  mixins: [productMixin],
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      originUser: null, // user data from server
      form: {
        provider: {
          id: null,
        },
        products: [],
        roles: [],
        externalProviders: [],
      },
      providers: null,
      loading: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters('products', [
      'products',
    ]),
    ...mapState('providers', [
      'providersList',
      'providerSearching',
    ]),
    ...mapGetters('providers', [
      'providerName',
    ]),
    ...mapGetters('me', [
      'canSwitchProvider',
      'meProviderName',
      'meProviderId',
    ]),
    rules() {
      return {
        firstName: [
          {
            required: true,
            message: 'This field is required',
          },
        ],
        lastName: [
          {
            required: true,
            message: 'This field is required',
          },
        ],
        email: [
          {
            required: true,
            message: 'This field is required',
          },
        ],
        phone: [
          {
            required: true,
            message: 'This field is required',
          },
          {
            validator: (rule, value, callback) => {
              value = String(value).replace(/\s/g, '')
              if (phoneRE.test(value)) {
                callback()
              } else {callback(new Error('Phone number not valid, must start with 04 and contain 8 digits'))}
            },
          },
        ],
        products: [
          {
            type: 'array',
            required: true,
            message: 'At least one selected product is required',
            trigger: 'change',
          },
          {
            validator: (rule, value, callback) => {
              const versionIsNotSelected = value.find(item => {
                return !item.versionId && !item.latest
              })
              if (versionIsNotSelected) {
                return callback(new Error('Product version is required'))
              } else {
                return callback()
              }
            },
            trigger: ['blur', 'change'],
          },
        ],
      }
    },
    back() {
      return this.id
        ? {
          name: 'user',
          params: {
            id: this.id,
          },
        } : {
          name: 'users',
        }
    },
    localProvidersList() {
      return this.providers || this.providersList
    },
    isGoogle() {
      return this.form.authType === 'google'
    },
  },
  created() {
    if (this.id) this.getData()
    else {
      this.form.active = true
      set(this.form, 'provider.id', this.meProviderId)

      if (this.meProviderId) {
        this.providers = [{
          value: this.meProviderId,
          label: this.meProviderName,
        }]
      }
    }
  },
  methods: {
    ...mapActions('user', [
      'getUser',
      'createUser',
      'updateUser',
    ]),
    ...mapActions('providers', [
      'searchProviders',
    ]),
    async getData() {
      this.loading = true

      const [error, response] = await this.getUser(this.id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Request error',
          message: error.message,
        })
      } else if (response) {
        this.originUser = cloneDeep(response)
        this.form = {
          ...this.form,
          ...response,
          provider: {
            id: get(response, 'provider.id'),
          },
          products: this.prefillProductsFields(response),
          roles: this.prefilRolesFields(response),
        }
        this.providers = [{
          label: get(response, 'provider.name'),
          value: get(response, 'provider.id'),
        }]
      }

      this.loading = false
    },
    prefillProductsFields(response) {
      return response.products.map(p => {
        const { version } = p
        let versionId = null
        let latest = false

        if (version) {
          versionId = version.id
          latest = version.latest
        } else {
          versionId = 'latest'
          latest = true
        }

        return {
          productId: p.product.id,
          versionId: versionId,
          latest,
        }
      })
    },
    prefilRolesFields(response) {
      const { roles } = response
      const acc = []
      this.activeProducts.forEach((item, index) => {
        roles.forEach(r => {
          if (item.roles.find(item => item.id === r.id)) {
            acc[index] = acc[index] || []
            acc[index].push(r.id)
          }
        })
      })
      return acc
    },
    onCancel() {
      this.$router.push(this.back)
    },
    async onSave() {
      const formValid = await this.$refs.form.validate().catch(() => false)

      if (!formValid) {
        const fields = ['firstName', 'lastName', 'email', 'phone']
        for (let i = 0; i < fields.length; i++) {
          let fieldInvalid
          this.$refs.form.validateField(fields[i], e => {
            if (e) {
              fieldInvalid = true
              const field = this.$refs[fields[i]].$el
              field.scrollIntoView({ behavior: 'smooth' })
            }
          })
          if (fieldInvalid) break
        }
        return
      }

      const { id } = this
      const form = cloneDeep(this.form)

      this.saving = true

      form.phone = form.phone.replace(/\s/g, '')
      form.products.forEach(pr => {
        if (pr.versionId === 'latest' || pr.latest === true) delete pr.versionId
      })

      form.roles = flatten(form.roles)

      const requestOptions = {
        headers: {
          'provider-id': form.provider.id || 'null',
        },
      }

      delete form.provider

      const response = this.id
        ? await this.updateUser({
          id,
          form,
          options: requestOptions,
        })
        : await this.createUser({
          form,
          options: requestOptions,
        })

      this.$notifier({
        response,
        errorMsg: 'Request Error',
        successTitle: `User ${this.id ? 'edited' : 'created'}`,
        successMsg: `User successfully ${this.id ? 'edited' : 'created'}`,
      }).then(success => {
        if (success) {
          this.$router.push({
            name: 'user',
            params: {
              id: success.id,
            },
          })
        }
      })

      this.saving = false
    },
  },
}
</script>

<template>
  <main-layout
    :title="id ? 'Edit User' : 'Create User'"
    :loading="loading"
    :class="$style.root"
    :back="back"
  >
    <el-card :class="$style.root">
      <div class="info-block__wrapper">
        <div class="info-block">
          <span class="info-block__title">
            User Details
          </span>
        </div>
      </div>

      <el-form
        ref="form"
        class="modal-form"
        label-position="top"
        :model="form"
        :rules="rules"
        :class="$style.form"
      >
        <div class="united-field">
          <el-form-item
            ref="firstName"
            label="First Name"
            prop="firstName"
          >
            <el-input
              v-model="form.firstName"
              data-test="firstName"
              :disabled="isGoogle"
            />
          </el-form-item>
          <el-form-item
            ref="lastName"
            label="Last Name"
            prop="lastName"
          >
            <el-input
              v-model="form.lastName"
              data-test="lastName"
              :disabled="isGoogle"
            />
          </el-form-item>
        </div>
        <div class="united-field">
          <el-form-item
            ref="email"
            label="Email Address"
            prop="email"
          >
            <el-input
              v-model="form.email"
              data-test="email"
              :disabled="isGoogle"
            />
          </el-form-item>
          <el-form-item
            ref="phone"
            label="Telephone"
            prop="phone"
          >
            <el-input
              v-model="form.phone"
              data-test="phone"
              placeholder="0400 000 000"
            />
          </el-form-item>
        </div>
        <div class="united-field">
          <el-form-item
            label="Provider"
            prop="providerId"
          >
            <el-select
              v-model="form.provider.id"
              :disabled="!!id || !!meProviderId"
              :remote-method="searchProviders"
              :loading="providerSearching"
              loading-text="Searching provider by name..."
              placeholder="Start typing provider name..."
              filterable
              remote
            >
              <el-option
                v-for="(item, idx) in localProvidersList"
                :key="idx"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            label="Status"
            prop="active"
          >
            <el-select
              v-model="form.active"
              :disabled="!id"
            >
              <el-option
                label="Active"
                :value="true"
              />
              <el-option
                label="Blocked"
                :value="false"
              />
            </el-select>
          </el-form-item>
        </div>

        <hr :class="['divider-primary', 'info-block__divider']">

        <products-edit
          :class="$style.products"
          :products="activeProducts"
          :form-products="form.products"
          :form-roles="form.roles"
          @check-product="checkProduct"
          @select-version="selectVersion"
        />

        <div>
          <el-button
            type="primary"
            plain
            :class="$style.button"
            @click="onCancel()"
          >
            Cancel
          </el-button>
          <el-button
            type="primary"
            :class="$style.button"
            :disabled="saving"
            @click="onSave()"
          >
            Save
          </el-button>
        </div>
      </el-form>
    </el-card>
  </main-layout>
</template>

<style lang="scss" module>
.root {
  position: relative;
}

.form {
  margin-top: rem(30px);
}

.products {
  :global {
    .el-form-item__label {
      margin-bottom: rem(30px);
      font-size: 1.4rem;
      font-weight: 600;
      color: #303133;

      &:after {
        display: none;
      }
    }
  }
}

.product {
  display: flex;
  align-items: center;
  width: 100%;
  margin: rem(0 0 20px);
}

.productCheckbox {
  width: rem(200px);
}

.productSelect{
  margin-left: rem(20px);
}

.button {
  width: rem(230px);
  margin-right: rem(30px);
}

</style>
